<!-- 
Panel for displaying the detail of an invoice.
-->
<template>
  <div>
    <our-busy-indicator :show="loading"/>
    <our-data-container>
      <our-data-field label="Levy collection">
        {{ invoice.levyCollection }} ({{ lookups.levyCollectionStatusDescriptions[invoice.levyCollectionStatus] }})
      </our-data-field>
      <our-data-field label="Provider">{{ invoice.provider }}</our-data-field>
    </our-data-container>
    <v-divider/>
    <our-data-container>
      <our-data-field :class="statusClass" label="Status">
        {{ lookups.invoiceStatusDescriptions[invoice.status] }}
      </our-data-field>
      <our-data-field :class="paymentStatusClass" label="Payment status" empty="Not applicable">
        {{ paymentStatusDescription }}
      </our-data-field>
      <our-data-field label="Amount">{{ invoice.amount | currency }}</our-data-field>
      <our-data-field label="SAP document identifier">{{ invoice.sapDocumentIdentifier }}</our-data-field>
      <our-data-field label="SAP customer identifier">{{ invoice.sapCustomerIdentifier }}</our-data-field>
      <our-data-field label="BPAY reference number">{{ invoice.bpayReferenceNumber }}</our-data-field>
      <our-data-field label="Created">{{ invoice.created | longDateTime }}</our-data-field>
      <our-data-field :class="dueClass" label="Due">
        {{ invoice.due | longDate }}
      </our-data-field>
      <our-data-field :class="clearedClass" label="Cleared" empty="Not cleared">
        {{ invoice.cleared | longDateTime }}
      </our-data-field>
      <our-data-field label="Outstanding amount">
        {{ invoice.outstandingAmount | currency }}
      </our-data-field>
      <our-data-field label="Transactions" label-width="10">
        <invoice-transactions :invoice-id="invoiceId" />
      </our-data-field>
      <our-data-field label="Last reconciled with SAP" empty="Never">
        {{ invoice.lastReconciled | longDateTime }}
      </our-data-field>
    </our-data-container>
  </div>
</template>

<script>
import moment from "moment";
import InvoiceTransactions from "@/components/invoices/InvoiceTransactions.vue";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";
import * as action from "@/store/invoices/action-types";

export default {
  components: { InvoiceTransactions },
  mixins: [lookupMixins, filterMixins],
  props: {
    invoiceId: {
      type: Number,
      required: true
    }
  },
  computed: {
    state() {
      return this.$store.state.invoices.view;
    },
    invoice() {
      return this.state.data;
    },
    loading() {
      return this.state.loading;
    },
    title() {
      return `Invoice ${this.invoice.sapDocumentIdentifier}`;
    },
    subtitle() {
      return `${this.invoice.levyCollection}, ${this.invoice.provider}`;
    },
    hasWarning() {
      return this.invoice.providerStatus == "InvoiceRaised";
    },
    paymentStatusDescription() {
      if (this.reversed) return null;
      const status = this.invoice.paymentStatus;
      const description = this.lookups.paymentStatusDescriptions[status];
      return status == "PaidLate" ? `${description} (${this.invoice.providerLatePaymentDays} days)` : description;
    },
    paymentStatusClass() {
      if (this.overdue) return "error-status";
      if (this.invoice.paymentStatus == "PaidButMightBeLate") return "warning-status";
      return null;
    },
    reversed() {
      return this.invoice.status == "Reversed";
    },
    overdue() {
      const s = this.invoice.paymentStatus;
      return (s == "Overdue" || s == "SlightlyOverdue") && !this.reversed;
    },
    statusClass() {
      return this.isValidStatus ? (this.invoice.status == "Cleared" ? "okay-status" : "") : "error-status";
    },
    isValidStatus() {
      return this.doesLookupHaveAttribute(this.invoice.status, "InvoiceIsValid", this.lookups.invoiceStatuses);
    },
    isOpen() {
      return this.isValidStatus && this.invoice.status == "Open";
    },
    dueClass() {
      const today = moment().startOf("day");
      if (this.isOpen && today > moment(this.invoice.due)) {
        return "error-status";
      }
      if (this.isOpen && today.add(5, "d") > moment(this.invoice.due)) {
        return "warning-status";
      }
      if (this.isOpen && moment(this.invoice.due) > today) {
        return "okay-status";
      }
      return null;
    },
    clearedClass() {
      return this.invoice.late == true ? "error-status" : "";
    }
  },
  watch: {
    invoiceId() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$store.dispatch(action.VIEW_REFRESH, this.invoiceId);
    }
  }
};
</script>

<style lang="scss">
.error-status {
  color: red;
}
.okay-status {
  color: green;
}
.warning-status {
  color: orange;
}
</style>